import { Box, Button, Grid, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FlexBetween from "../../components/FlexBetween";
import { addEditIssueClassificationSchema } from "../../schemas";
import useFetchIssueClassificationById from "../../hooks/useFetchClassificationById";
import {
  addIssueClassification,
  updateIssueClassification,
} from "../../services/classificationService";
import Loader from "../../components/Molecules/Loader/Loader";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import { IIssueClassification } from "../../types/Classification";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { useToast } from "../../services/utils";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";
import { useAuth } from "../../contexts/AuthContext";

const AddEditClassification: React.FC = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isAdmin } = useAuth();
  const { id: classificationId } = useParams();
  const isEditMode = Boolean(classificationId);

  const [formData, setFormData] = useState<IIssueClassification>({
    issue_classification_name: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [initialFormData, setInitialFormData] = useState<any>({});
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] =
    useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);

  const [classificationData] = useFetchIssueClassificationById({
    setIsLoading,
    currentClassificationId: classificationId,
  });

  const buildPayload = () => {
    return {
      issue_classification: {
        issue_classification_name: formData.issue_classification_name,
        is_active: true,
      },
    };
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: isEditMode
          ? `/issue-classification/${classificationId}`
          : "/issue-classification",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id: classificationId } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast("Payload is valid!", "success");
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    const payload = buildPayload();
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    showToast("Payload copied to clipboard!", "success");
  };

  const handleFormData = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateField = async (name: string, value: any) => {
    try {
      await addEditIssueClassificationSchema.validateAt(name, {
        [name]: value,
      });
      setErrors((prevErrors: any) => ({ ...prevErrors, [name]: undefined }));
    } catch (validationError: any) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  const onSaveClassification = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await addEditIssueClassificationSchema.validate(formData, {
        abortEarly: false,
      });

      // Build the payload with the required structure - always send is_active: true
      const reqBody = {
        issue_classification: {
          issue_classification_name: formData.issue_classification_name,
          is_active: true,
        },
      };

      let response: any;
      if (classificationId) {
        response = await updateIssueClassification({
          currentClassificationId: classificationId,
          payload: reqBody,
        });
      } else {
        response = await addIssueClassification({ payload: reqBody });
      }
      if (response) {
        showToast(
          `Classification ${
            classificationId ? "updated" : "created"
          } successfully!`,
          "success"
        );
        navigate("/classification");
      }
    } catch (validationErrors: any) {
      if (validationErrors.inner) {
        const newErrors: { [key: string]: string } = {};
        validationErrors.inner.forEach(
          (error: { path: string | number; message: string }) => {
            newErrors[error.path] = error.message;
          }
        );
        setErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));
      } else {
        console.error(
          `Cannot ${classificationId ? "update" : "create"} classification:`,
          validationErrors
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (classificationData && classificationData?.id) {
      setFormData({
        issue_classification_name:
          classificationData.issue_classification_name || "",
      });
      setInitialFormData(
        JSON.parse(
          JSON.stringify({
            issue_classification_name:
              classificationData.issue_classification_name || "",
          })
        )
      );
    } else if (!classificationId) {
      setInitialFormData({ issue_classification_name: "" });
    }
  }, [classificationData, classificationId]);

  useEffect(() => {
    if (classificationId && classificationData?.issue_classification_name) {
      setCurrentBreadcrumbPage({
        name: `Edit ${classificationData.issue_classification_name}`,
        id: classificationData.id,
      });
    } else {
      setCurrentBreadcrumbPage({
        name: classificationId ? "Edit Classification" : "Add Classification",
        id: classificationId || null,
      });
    }
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [classificationId, classificationData, setCurrentBreadcrumbPage]);

  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      setHasChanges(
        JSON.stringify(formData) !== JSON.stringify(initialFormData)
      );
    } else {
      setHasChanges(false);
    }
  }, [formData, initialFormData]);

  return (
    <Box>
      <Loader isLoading={isLoading} />
      <form onSubmit={onSaveClassification} autoComplete="off">
        <Box className="text-box-card compact-text-box-card">
          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                Classification Name
                <span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="issue_classification_name"
                onChange={handleFormData}
                className={`form-control ${
                  errors?.issue_classification_name ? "has-error" : ""
                }`}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                error={!!errors?.issue_classification_name}
                helperText={errors?.issue_classification_name || ""}
                value={formData?.issue_classification_name}
                placeholder="Enter classification name"
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <FlexBetween
            gap="3rem"
            sx={{ marginTop: 4, display: "flex", justifyContent: "flex-end" }}
          >
            <ViewJsonSplitButton
              onViewJson={() => {
                setValidationResponse(null);
                setIsPayloadDialogOpen(true);
              }}
              onCopyJson={handleCopyPayload}
            />
            <Button
              type="submit"
              className="btn-orange"
              color="secondary"
              variant="contained"
              disabled={isEditMode ? !hasChanges : false}
            >
              <SaveOutlinedIcon /> &nbsp; {isEditMode ? "Update" : "Save"}
            </Button>
          </FlexBetween>
        </Box>
      </form>

      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle="Classification JSON"
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </Box>
  );
};

export default AddEditClassification;
